'use client';
/**
 * R14b ?r= 필터 섬 — 게시판·블로그·홈 목록의 «지역 필터 보기».
 *
 *   <RegionParamRobots basePath="/board" />          페이지당 1개: ?r= 가 있으면 robots=noindex,follow(수화 뒤 head 주입)
 *   <RegionFilterSlot kind="talk" limit={5}>{서버 목록}</RegionFilterSlot>
 *
 * 규칙(NAV14 §R14b · 결정 기록은 보고서):
 *   · 서버 페이지는 searchParams 를 읽지 않는다(ƒ 불변 · ISR 유지). 서버 HTML = 전국 목록 그대로(봇·일반 동일).
 *   · ?r= 는 useEffect 뒤에만 읽는다(use-region-param.ts). 유효한 지역이면 기존 공개 통로로 그 지역 목록을 받아
 *     서버 목록 «자리» 에 그린다(같은 템플릿·같은 섹션). 실패·무효 지역이면 서버 목록을 그대로 둔다.
 *   · canonical 은 서버 메타가 이미 파라미터 없는 경로. ?r= 링크는 어디에도 <a href> 로 내보내지 않는다.
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useEffect, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useRegionParam, applyNoindex, emitRegionParam } from '@/lib/nav/use-region-param';
import { writeRegionMemory } from '@/lib/nav/region-scope';
// ⚠️ 조회 모듈(Supabase 브라우저 클라이언트 포함)은 ?r= 가 있을 때만 동적 import — 첫 로드 JS·LCP 불변.
import type { FilterItem } from '@/lib/nav/region-filter-client';
import type { FilterScope } from '@/lib/nav/region-filter';
import { REGION_MEMORY_EVENT } from './GlobalRegionChip';
import './global-nav.css';

export function RegionParamRobots({ basePath }: { basePath: string }) {
  const code = useRegionParam();
  useEffect(() => {
    if (code === undefined) return;
    applyNoindex(code !== null, basePath);
  }, [code, basePath]);
  return null;
}

export type RegionFilterKind = 'upcoming' | 'analysis' | 'talk' | 'board';

export interface RegionFilterSlotProps {
  kind: RegionFilterKind;
  limit: number;
  /** 필터를 풀 때 돌아갈 파라미터 없는 경로. */
  basePath: string;
  children?: ReactNode;
}

type State = { code: string; scope: FilterScope; items: FilterItem[] } | null;

const client = () => import('@/lib/nav/region-filter-client');

async function load(kind: RegionFilterKind, scope: FilterScope, limit: number): Promise<FilterItem[] | null> {
  const m = await client();
  if (kind === 'upcoming') return m.fetchRegionUpcoming(scope, limit);
  if (kind === 'analysis') return m.fetchRegionAnalysis(scope, limit);
  return m.fetchRegionTalk(scope, limit, kind === 'talk');
}

const NOUN: Record<RegionFilterKind, string> = { upcoming: '공고 전 현장', analysis: '분석 글', talk: '토론방 글', board: '글' };

export default function RegionFilterSlot({ kind, limit, basePath, children }: RegionFilterSlotProps) {
  const router = useRouter();
  const code = useRegionParam();
  const [state, setState] = useState<State>(null);

  useEffect(() => {
    let alive = true;
    if (!code) { setState(null); return; }
    (async () => {
      const scope = await (await client()).loadFilterScope(code);
      if (!alive || !scope) { if (alive) setState(null); return; }
      const items = await load(kind, scope, limit);
      if (!alive) return;
      setState(items ? { code, scope, items } : null);
    })();
    return () => { alive = false; };
  }, [code, kind, limit]);

  if (!state || state.code !== code) return <>{children}</>;

  const clear = () => {
    writeRegionMemory(null);
    try { window.dispatchEvent(new Event(REGION_MEMORY_EVENT)); } catch { /* 무시 */ }
    router.push(encodeURI(basePath));
    emitRegionParam(null);
  };

  return (
    <div className="kd-rf" data-kd-region-filter={state.code}>
      <p className="kd-rf-note">
        <span><b>{state.scope.label}</b> {NOUN[kind]} {state.items.length ? `${state.items.length}건` : '없음'}</span>
        <button type="button" className="kd-gn-btn" onClick={clear}>전국 보기</button>
        {kind === 'talk' || kind === 'board' ? <a href={state.scope.boardHref}>{state.scope.label} 게시판 전체 <span aria-hidden="true">→</span></a> : null}
      </p>
      {state.items.length ? (
        <ul className="kd-rf-list">
          {state.items.map((it) => (
            <li key={it.key}>
              <a href={it.href}>
                <span>{it.title}</span>
                {it.meta ? <small>{it.meta}</small> : null}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="kd-rf-note">이 지역에는 아직 {NOUN[kind]}이 없어요. 전국 목록은 「전국 보기」로 볼 수 있어요.</p>
      )}
    </div>
  );
}
